import { useCallback, useEffect, useRef } from 'react';

export interface UseSearchFocusTrackingReturn {
  handleEnsureDataLoaded: () => void;
  wrapperRef: React.RefObject<HTMLDivElement>;
}

export const useSearchFocusTracking = (
  setSelectedGlAccount: (label: string) => void,
  query: string,
  setQuery: (q: string) => void
): UseSearchFocusTrackingReturn => {
  const hasUserInteractedRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const queryRef = useRef(query);
  queryRef.current = query;

  const handleEnsureDataLoaded = useCallback(() => {
    if (!hasUserInteractedRef.current) {
      setSelectedGlAccount('Flagship');
      hasUserInteractedRef.current = true;
    }
  }, [setSelectedGlAccount]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT') {
        if (!hasUserInteractedRef.current) {
          setSelectedGlAccount('Flagship');
          hasUserInteractedRef.current = true;
        }
        const currentValue = (target as HTMLInputElement).value?.trim() || '';
        if (currentValue !== queryRef.current) {
          setQuery(currentValue);
        }
      }
    };

    const handleFocusOut = () => {
      setTimeout(() => {
        const activeEl = document.activeElement;
        if (!wrapper.contains(activeEl)) {
          hasUserInteractedRef.current = false;
        }
      }, 0);
    };

    wrapper.addEventListener('focusin', handleFocusIn);
    wrapper.addEventListener('focusout', handleFocusOut);
    return () => {
      wrapper.removeEventListener('focusin', handleFocusIn);
      wrapper.removeEventListener('focusout', handleFocusOut);
    };
  }, [setSelectedGlAccount, setQuery]);

  return { handleEnsureDataLoaded, wrapperRef };
};
